// src/components/InventoryDisplay.js
import React, { useState } from 'react';
import styles from './InventoryDisplay.module.css';
import { ItemData } from '../data/ItemData';
import { getItemImageSrc } from '../utils/getItemImageSrc';
import ItemTooltip from './ItemTooltip';

// Accept lock functions
const InventoryDisplay = ({ playerData, setPlayerData, onEquipItem, onUseItem, onToggleItemLock, isItemLocked }) => {
  const currentInventory = playerData.inventory || {};

  const firstAvailableItemId = Object.keys(currentInventory).find(id => {
    const item = currentInventory[id];
    return (typeof item === 'object' && item !== null) || (typeof item === 'number' && item > 0);
  });
  const [selectedItemId, setSelectedItemId] = useState(firstAvailableItemId || null);

  const handleItemClick = (itemId) => {
    setSelectedItemId(itemId);
  };

  const selectedItemInstance = selectedItemId ? currentInventory[selectedItemId] : null;
  let itemKeyForBaseData = selectedItemId;
  if (selectedItemInstance && typeof selectedItemInstance === 'object' && selectedItemInstance.key) {
    itemKeyForBaseData = selectedItemInstance.key;
  }
  const selectedItemBaseData = selectedItemInstance ? ItemData[itemKeyForBaseData] : null;


  const itemsForGrid = Object.entries(currentInventory)
    .map(([idOrKey, itemDataOrCount]) => {
        if (typeof itemDataOrCount === 'object' && itemDataOrCount !== null) { // Unique item
            const isEquipped = Object.values(playerData.equipment).includes(idOrKey); //
            if (isEquipped) return null;
            return { ...itemDataOrCount, id: idOrKey, isStackable: false, key: itemDataOrCount.key };
        } else if (typeof itemDataOrCount === 'number' && itemDataOrCount > 0) { // Stackable item
            const baseData = ItemData[idOrKey]; //
            if (!baseData) {
                console.warn(`[InventoryDisplay] ItemData missing for key: ${idOrKey}`);
                return null;
            }
            return { ...baseData, id: idOrKey, key: idOrKey, quantity: itemDataOrCount, isStackable: true };
        }
        return null;
    })
    .filter(item => item !== null);

  // Calculate total slots based on items, with a minimum of 35 slots for better layout
  const totalSlots = Math.max(35, itemsForGrid.length + 10);


  return (
    <div className={styles.inventoryDisplayContainer}>
      <div className={styles.headerControls}>
        <h3 className={styles.title}>Main Inventory</h3>
      </div>

      <div className={styles.inventoryLayout}>
        <div className={styles.itemGridWrapper}>
          <div className={styles.itemGrid}>
            {itemsForGrid.map(item => (
              <ItemTooltip
                key={item.id}
                itemKey={item.key}
                itemInstance={item}
                onEquipItem={onEquipItem}
                onUseItem={onUseItem}
              >
                <div
                  className={`${styles.inventoryItem} ${selectedItemId === item.id ? styles.selectedItem : ''}`}
                  onClick={() => handleItemClick(item.id)}
                >
                  <img src={getItemImageSrc(item.key)} alt={item.name || ItemData[item.key]?.name} className={styles.itemIcon} />
                  {item.isStackable && item.quantity > 1 && (
                    <span className={styles.itemQuantity}>{item.quantity}</span>
                  )}
                  {!item.isStackable && item.durability !== null && item.durability !== undefined && (
                    <span className={`${styles.itemDurability} ${item.durability <= 0 ? styles.broken : ''}`}>
                      {item.durability}/{item.maxDurability}
                    </span>
                  )}
                  {isItemLocked && isItemLocked(item.id) && (
                    <span className={styles.lockIcon}>🔒</span>
                  )}
                </div>
              </ItemTooltip>
            ))}
            {[...Array(Math.max(0, totalSlots - itemsForGrid.length))].map((_, i) => (
              <div key={`empty-${i}`} className={`${styles.inventoryItem} ${styles.inventoryItemEmpty}`}></div>
            ))}
          </div>
        </div>

        {selectedItemInstance && selectedItemBaseData ? (
          <div className={styles.itemDetails}>
            <h4 className={styles.selectedItemName}>
              {selectedItemBaseData.name} {/* */}
              {typeof selectedItemInstance === 'number' && selectedItemInstance > 0 && ` (x${selectedItemInstance})`}
              {typeof selectedItemInstance === 'object' && selectedItemInstance.quantity > 1 && ` (x${selectedItemInstance.quantity})`}
            </h4>
            <img src={getItemImageSrc(selectedItemBaseData.key || selectedItemId)} alt={selectedItemBaseData.name} className={styles.selectedItemImage} /> {/* */}
            <p className={styles.itemDescription}>{selectedItemBaseData.description || "No description."}</p> {/* */}

            <div className={styles.detailGroup}>
              <div className={styles.detailRow}><span>Type:</span> <span>{selectedItemBaseData.type}</span></div> {/* */}
              {selectedItemBaseData.goldValue !== undefined && <div className={styles.detailRow}><span>Value:</span> <span>{selectedItemBaseData.goldValue} G</span></div>} {/* */}
              {selectedItemBaseData.weight !== undefined && <div className={styles.detailRow}><span>Weight:</span> <span>{selectedItemBaseData.weight}</span></div>} {/* */}
            </div>

            {typeof selectedItemInstance === 'object' && selectedItemInstance.maxDurability !== null && selectedItemInstance.maxDurability !== undefined && ( //
              <div className={`${styles.detailGroup} ${styles.condition}`}> {/* */}
                <div className={styles.detailRow}>
                  <span>Condition:</span>
                  <span>{selectedItemInstance.durability} / {selectedItemInstance.maxDurability}</span>
                </div>
                <div className={styles.durabilityBarContainer}> {/* */}
                  <div
                    className={styles.durabilityBar} //
                    style={{ width: `${(selectedItemInstance.durability / selectedItemInstance.maxDurability) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className={styles.actionButtons}>
              {selectedItemBaseData.type === 'consumable' && ( //
                <button
                  className={`${styles.button} ${styles.useButton}`}
                  onClick={() => {
                    // For stackable items, selectedItemId is the key.
                    // For unique non-stackable consumables (if any), selectedItemId is the unique ID.
                    // handleUseItem in Layout.js will need to correctly interpret this.
                    onUseItem(selectedItemId);
                  }}
                >
                  Use
                </button>
              )}
              {typeof selectedItemInstance === 'object' && selectedItemBaseData.slotType && ( //
                <button
                  className={`${styles.button} ${styles.equipButton}`} //
                  onClick={() => onEquipItem(selectedItemId)}
                  disabled={selectedItemInstance.durability !== undefined && selectedItemInstance.durability <=0} //
                >
                  Equip
                </button>
              )}
              {onToggleItemLock && (
                <button
                  className={`${styles.button} ${isItemLocked && isItemLocked(selectedItemId) ? styles.unlockButton : styles.lockButton}`}
                  onClick={() => onToggleItemLock(selectedItemId)}
                >
                  {isItemLocked && isItemLocked(selectedItemId) ? '🔓 Unlock' : '🔒 Lock'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.noItemSelected}><p>Select an item to view details.</p></div> //
        )}
      </div>
    </div>
  );
};

export default InventoryDisplay;